import axios from 'axios';
import { accountingApi } from '../config/apiClient';
import { API_BASE_URL } from '../config/api';

// ============================================================================
// ÇEK/SENET SERVICE
// ============================================================================

// Tür sabitleri
export const CheckNoteType = {
  CHECK: 'CHECK',
  PROMISSORY_NOTE: 'PROMISSORY_NOTE',
} as const;

// Yön sabitleri
export const CheckNoteDirection = {
  RECEIVED: 'RECEIVED',
  GIVEN: 'GIVEN',
} as const;

// Durum sabitleri
export const CheckNoteStatus = {
  // Alınan çekler için durumlar
  PORTFOLIO: 'PORTFOLIO',
  IN_COLLECTION: 'IN_COLLECTION',
  COLLECTED: 'COLLECTED',
  PARTIAL_COLLECTED: 'PARTIAL_COLLECTED',
  ENDORSED: 'ENDORSED',
  COLLATERAL: 'COLLATERAL',
  COLLATERAL_RETURNED: 'COLLATERAL_RETURNED',
  BOUNCED: 'BOUNCED',
  RETURNED: 'RETURNED',
  CANCELED: 'CANCELED',
  // Verilen çekler için durumlar
  GIVEN_PENDING: 'GIVEN_PENDING',
  GIVEN_PAID: 'GIVEN_PAID',
  GIVEN_RETURNED: 'GIVEN_RETURNED',
} as const;

// Type exports
export type CheckNoteTypeValue = typeof CheckNoteType[keyof typeof CheckNoteType];
export type CheckNoteDirectionValue = typeof CheckNoteDirection[keyof typeof CheckNoteDirection];
export type CheckNoteStatusValue = typeof CheckNoteStatus[keyof typeof CheckNoteStatus];

// ============================================================================
// DTO INTERFACES
// ============================================================================

export interface CheckNoteDto {
  id: number;
  companyId: string;
  batchId?: number;
  documentNumber: string;
  type: CheckNoteTypeValue;
  direction: CheckNoteDirectionValue;
  
  // Cari bilgileri
  accountId: number;
  accountName?: string;
  
  // Çek bilgileri
  bankName?: string;
  bankBranchName?: string;
  bankBranchCode?: string;
  checkNumber?: string;
  checkAccountNumber?: string;
  
  // Senet bilgileri
  drawerName?: string;
  guarantorName?: string;
  guarantorIdentityNo?: string;
  guarantorAddress?: string;
  debtorIdentityNo?: string;
  debtorAddress?: string;
  jurisdictionPlace?: string;
  
  // Protesto bilgileri
  protestedDate?: string;
  protestedNotary?: string;
  protestedReason?: string;
  legalStatus?: string;
  legalFileNumber?: string;
  
  // Tarih ve tutar
  issueDate: string;
  issuePlace?: string;
  dueDate: string;
  amount: number;
  currency: string;
  exchangeRate: number;
  amountTRY: number;
  
  // Durum
  status: CheckNoteStatusValue;
  statusDate: string;
  
  // Tahsilat bilgileri
  collectedAmount: number;
  remainingAmount: number;
  collectionDate?: string;
  
  // Ciro bilgileri
  endorsedToAccountId?: number;
  endorsedToAccountName?: string;
  endorsedDate?: string;
  
  // Diğer
  notes?: string;
  branchId?: string;
  createdAt: string;
  createdBy?: string;

  // PYS: proje bağı
  projectId?: number | null;
  projectName?: string | null;
  
  // Hesaplanan alanlar
  daysUntilDue: number;
  isOverdue: boolean;
}

export interface CreateCheckNoteDto {
  /** İsteğe bağlı; accounting API tenant/context ile de doldurulur */
  companyId?: string;
  type: CheckNoteTypeValue;
  direction: CheckNoteDirectionValue;
  accountId?: number; // Alınan için zorunlu, verilen için opsiyonel
  accountName?: string;
  
  // Çek bilgileri
  bankName?: string;
  bankBranchName?: string;
  bankBranchCode?: string;
  checkNumber?: string;
  checkAccountNumber?: string;
  
  // Verilen çek için (Direction = GIVEN)
  bankAccountId?: number;
  subAccountId?: number;
  checkBookId?: number;
  
  // Senet bilgileri
  drawerName?: string;
  guarantorName?: string;
  guarantorIdentityNo?: string;
  guarantorAddress?: string;
  debtorIdentityNo?: string;
  debtorAddress?: string;
  jurisdictionPlace?: string;
  
  // Tarih ve tutar
  issueDate: string;
  issuePlace?: string;
  dueDate: string;
  amount: number;
  currency: string;
  exchangeRate: number;
  
  notes?: string;
  branchId?: string;
  createdBy?: string;

  /** PYS: bağlanacak proje (opsiyonel; null → bağ yok) */
  projectId?: number | null;
}

export interface UpdateCheckNoteDto extends CreateCheckNoteDto {
  id: number;
  updatedBy?: string;
}

export interface DeleteCheckNoteDto {
  id: number;
}

export interface ChangeCheckNoteStatusDto {
  id: number;
  newStatus: CheckNoteStatusValue;
  bankAccountId?: number;
  bankSubAccountId?: number; // Alt hesap ID'si
  cashboxId?: number;
  endorsedToAccountId?: number;
  endorsedToAccountName?: string;
  transactionDate?: string;
  exchangeRate?: number;
  bouncedReason?: string;
  amount?: number; // Kısmi tahsil tutarı
  description?: string;
  createdBy?: string;
}

export interface CheckNoteFilterDto {
  type?: CheckNoteTypeValue | 'ALL';
  direction?: CheckNoteDirectionValue | 'ALL';
  status?: CheckNoteStatusValue | 'ALL';
  accountId?: number;
  dueDateStart?: string;
  dueDateEnd?: string;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

// Seri Senet DTO'ları
export interface CreateBatchPromissoryNotesDto {
  /**
   * Gönderilmesi gerekir: backend DTO'sunda CompanyId [Required] olduğu için
   * boş gövde model doğrulamasında (controller çalışmadan) 400 döner. Sunucu
   * değeri yine tenant header'ından ezer, burada gönderilmesi yalnızca
   * doğrulamayı geçmek içindir.
   */
  companyId?: string;
  direction: CheckNoteDirectionValue;
  accountId: number;
  accountName?: string;
  drawerName?: string;
  
  // Senet bilgileri
  debtorIdentityNo?: string;
  debtorAddress?: string;
  guarantorName?: string;
  guarantorIdentityNo?: string;
  guarantorAddress?: string;
  jurisdictionPlace?: string;
  issuePlace?: string;
  
  count: number;
  amountPerNote?: number;
  totalAmount?: number;
  currency: string;
  exchangeRate?: number;
  firstDueDate: string;
  dueDateIntervalDays: number;
  // Belge numarası sunucuda üretilir (SN-YYYY-XXXXX); istekle numara gönderilmez.
  description?: string;
  customAmounts?: number[]; // Her senedin özel tutarı (opsiyonel)
  createdBy?: string;
}

export interface BatchPromissoryNotesResponseDto {
  createdCount: number;
  totalAmount: number;
  notes: CheckNoteDto[];
  firstDueDate: string;
  lastDueDate: string;
}

export interface CheckNoteSummaryDto {
  // Alınan çekler
  receivedCheckCount: number;
  receivedCheckTotal: number;
  receivedCheckTotalTRY: number;
  
  // Verilen çekler
  givenCheckCount: number;
  givenCheckTotal: number;
  givenCheckTotalTRY: number;
  
  // Alınan senetler
  receivedNoteCount: number;
  receivedNoteTotal: number;
  receivedNoteTotalTRY: number;
  
  // Verilen senetler
  givenNoteCount: number;
  givenNoteTotal: number;
  givenNoteTotalTRY: number;
  
  // Durum bazlı
  portfolioCount: number;
  portfolioTotal: number;
  inCollectionCount: number;
  inCollectionTotal: number;
  collectedCount: number;
  collectedTotal: number;
  endorsedCount: number;
  endorsedTotal: number;
  bouncedCount: number;
  bouncedTotal: number;
  
  // Vade bazlı
  overdueCount: number;
  overdueTotal: number;
  dueTodayCount: number;
  dueTodayTotal: number;
  dueThisWeekCount: number;
  dueThisWeekTotal: number;
}

export interface CheckNoteListResponseDto {
  items: CheckNoteDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: CheckNoteSummaryDto;
}

export interface UpcomingDueDateDto {
  date: string;
  checkCount: number;
  checkTotal: number;
  noteCount: number;
  noteTotal: number;
}

export interface CheckNoteDashboardDto {
  summary: CheckNoteSummaryDto;
  upcomingDueDates: UpcomingDueDateDto[];
  recentTransactions: CheckNoteDto[];
}

// İşlem geçmişi
export interface CheckNoteTransactionDto {
  id: number;
  checkNoteId: number;
  transactionDate: string;
  transactionType: string;
  fromStatus?: string;
  toStatus?: string;
  amount?: number;
  currency?: string;
  relatedAccountId?: number;
  relatedAccountName?: string;
  bankAccountId?: number;
  bankAccountName?: string;
  cashboxId?: number;
  referenceNumber?: string;
  description?: string;
  createdBy?: string;
  createdAt: string;
}

// ============================================================================
// API RESPONSE INTERFACE
// ============================================================================

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Şirkete ait çek/senetleri filtreli getir
 */
export const getCheckNotes = async (filter: CheckNoteFilterDto): Promise<CheckNoteListResponseDto> => {
  const params = new URLSearchParams();
  
  if (filter.type && filter.type !== 'ALL') params.append('type', filter.type);
  if (filter.direction && filter.direction !== 'ALL') params.append('direction', filter.direction);
  if (filter.status && filter.status !== 'ALL') params.append('status', filter.status);
  if (filter.accountId) params.append('accountId', filter.accountId.toString());
  if (filter.dueDateStart) params.append('dueDateStart', filter.dueDateStart);
  if (filter.dueDateEnd) params.append('dueDateEnd', filter.dueDateEnd);
  if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
  if (filter.page) params.append('page', filter.page.toString());
  if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
  if (filter.sortBy) params.append('sortBy', filter.sortBy);
  if (filter.sortDirection) params.append('sortDirection', filter.sortDirection);
  
  const response = await accountingApi.get<ApiResponse<CheckNoteListResponseDto>>(
    `${API_BASE_URL}/api/accounting/CheckNote/by-company?${params.toString()}`
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Çek/Senetler getirilemedi');
};

/**
 * POST ile filtreli çek/senet listesi getir (karmaşık filtreler için)
 */
export const filterCheckNotes = async (filter: CheckNoteFilterDto): Promise<CheckNoteListResponseDto> => {
  const response = await accountingApi.post<ApiResponse<CheckNoteListResponseDto>>(
    `${API_BASE_URL}/api/accounting/CheckNote/filter`,
    filter
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Çek/Senetler filtrenemedi');
};

/**
 * ID'ye göre çek/senet getir
 */
export const getCheckNoteById = async (id: number): Promise<CheckNoteDto> => {
  const response = await accountingApi.get<ApiResponse<CheckNoteDto>>(
    `${API_BASE_URL}/api/accounting/CheckNote/${id}`
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Çek/Senet bulunamadı');
};

/**
 * Çek/Senet işlem geçmişini getir
 */
export const getCheckNoteTransactions = async (id: number): Promise<CheckNoteTransactionDto[]> => {
  const response = await accountingApi.get<ApiResponse<CheckNoteTransactionDto[]>>(
    `${API_BASE_URL}/api/accounting/CheckNote/${id}/transactions`
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'İşlem geçmişi getirilemedi');
};

/**
 * Dashboard özeti getir
 */
export const getCheckNoteDashboard = async (): Promise<CheckNoteDashboardDto> => {
  const response = await accountingApi.get<ApiResponse<CheckNoteDashboardDto>>(
    `${API_BASE_URL}/api/accounting/CheckNote/dashboard`
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Dashboard getirilemedi');
};

/**
 * Sonraki belge numarasını önizle
 */
export const previewNextDocumentNumber = async (type: CheckNoteTypeValue): Promise<string> => {
  const response = await accountingApi.get<ApiResponse<string>>(
    `${API_BASE_URL}/api/accounting/CheckNote/preview-number?type=${type}`
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Belge numarası alınamadı');
};

/**
 * Çek numarasının mükerrer olup olmadığını kontrol et
 */
export interface DuplicateCheckResult {
  isDuplicate: boolean;
  firstAvailableNumber: string | null;
}

export const checkDuplicateCheckNumber = async (
  checkNumber: string,
  type?: string,
  excludeId?: number
): Promise<DuplicateCheckResult> => {
  const params = new URLSearchParams({ checkNumber });
  if (type) params.append('type', type);
  if (excludeId) params.append('excludeId', excludeId.toString());

  const response = await accountingApi.get<ApiResponse<DuplicateCheckResult>>(
    `${API_BASE_URL}/api/accounting/CheckNote/check-duplicate?${params.toString()}`
  );

  if (response.data.success !== undefined) {
    return response.data.data ?? { isDuplicate: false, firstAvailableNumber: null };
  }
  throw new Error(response.data.message || 'Mükerrer kontrol yapılamadı');
};

/**
 * Çek defterine ait kullanılmış çek yapraklarını getir
 */
export const getCheckNotesByCheckBook = async (
  checkBookId: number
): Promise<CheckNoteDto[]> => {
  const response = await accountingApi.get<ApiResponse<CheckNoteDto[]>>(
    `${API_BASE_URL}/api/accounting/CheckNote/by-checkbook/${checkBookId}`
  );

  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  return [];
};

/**
 * Yeni çek/senet oluştur
 */
export const createCheckNote = async (dto: CreateCheckNoteDto): Promise<CheckNoteDto> => {
  try {
    const response = await accountingApi.post<ApiResponse<CheckNoteDto>>(
      `${API_BASE_URL}/api/accounting/CheckNote/create`,
      dto
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error((response.data as any).message || 'Çek/Senet oluşturulamadı');
  } catch (e: any) {
    const d = e?.response?.data;
    const errs = d?.errors && typeof d.errors === 'object'
      ? Object.entries(d.errors).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n')
      : '';
    const msg = d?.title && errs ? `${d.title}\n${errs}` : (d?.message || d?.Message || e?.message);
    throw new Error(msg || 'Çek/Senet oluşturulamadı');
  }
};

/**
 * Çek/Senet güncelle
 */
export const updateCheckNote = async (dto: UpdateCheckNoteDto): Promise<CheckNoteDto> => {
  const response = await accountingApi.post<ApiResponse<CheckNoteDto>>(
    `${API_BASE_URL}/api/accounting/CheckNote/update`,
    dto
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Çek/Senet güncellenemedi');
};

/**
 * Çek/Senet sil (soft delete)
 */
export const deleteCheckNote = async (dto: DeleteCheckNoteDto): Promise<boolean> => {
  const response = await accountingApi.post<ApiResponse<boolean>>(
    `${API_BASE_URL}/api/accounting/CheckNote/delete`,
    dto
  );
  
  if (response.data.success) {
    return true;
  }
  throw new Error(response.data.message || 'Çek/Senet silinemedi');
};

// ============================================================================
// Çek/Senet Silme (İlişkili Hareketler) - Fatura silme mantığıyla aynı
// ============================================================================

export interface CheckNoteRelatedAccountTransaction {
  id: number;
  accountId: number;
  accountName: string;
  transactionType: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  balanceAfter: number;
  transactionDate: string;
  referenceType?: string;
}

export interface CheckNoteRelatedBankCashTransaction {
  id: number;
  targetId: number;
  targetName: string;
  transactionType: string;
  amount: number;
  balanceAfter: number;
  description: string;
  transactionDate: string;
}

export interface CheckNoteRelatedTransactions {
  checkNote: CheckNoteDto | null;
  accountTransactions: CheckNoteRelatedAccountTransaction[];
  bankTransactions: CheckNoteRelatedBankCashTransaction[];
  cashboxTransactions: CheckNoteRelatedBankCashTransaction[];
  totalAccountTransactions: number;
  totalBankTransactions: number;
  totalCashboxTransactions: number;
  totalDebitAmount: number;
  totalCreditAmount: number;
  totalBankAmount: number;
  totalCashAmount: number;
}

/**
 * Çek/Senete bağlı tüm hareketleri getir (silme önizleme modalı için).
 */
export const getCheckNoteRelatedTransactions = async (
  id: number
): Promise<CheckNoteRelatedTransactions> => {
  const response = await accountingApi.get<ApiResponse<CheckNoteRelatedTransactions>>(
    `${API_BASE_URL}/api/accounting/CheckNote/${id}/related-transactions`
  );

  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Çek/Senet hareketleri yüklenemedi');
};

/**
 * Çek/Senet ve tüm bağlı hareketleri (cari + banka + kasa) sil.
 */
export const deleteCheckNoteWithTransactions = async (
  id: number,
  deletedBy?: string
): Promise<boolean> => {
  const response = await accountingApi.post<ApiResponse<boolean>>(
    `${API_BASE_URL}/api/accounting/CheckNote/${id}/delete-with-transactions`,
    // Backend PascalCase bekler (CRITICAL_API_DEVELOPMENT_RULES). CompanyId GÖNDERİLMEZ;
    // backend RequireCompanyId() ile JWT/header'dan alır.
    { Id: id, DeletedBy: deletedBy }
  );

  if (response.data.success) {
    return true;
  }
  throw new Error(response.data.message || 'Çek/Senet silinemedi');
};

/**
 * Çek/Senet durumunu değiştir
 */
export const changeCheckNoteStatus = async (dto: ChangeCheckNoteStatusDto): Promise<CheckNoteDto> => {
  // PascalCase dönüşümü (Backend C# kullanıyor)
  const requestBody = {
    Id: dto.id,
    NewStatus: dto.newStatus,
    BankAccountId: dto.bankAccountId,
    BankSubAccountId: dto.bankSubAccountId,
    CashboxId: dto.cashboxId,
    EndorsedToAccountId: dto.endorsedToAccountId,
    EndorsedToAccountName: dto.endorsedToAccountName,
    TransactionDate: dto.transactionDate,
    ExchangeRate: dto.exchangeRate,
    BouncedReason: dto.bouncedReason,
    Amount: dto.amount, // Kısmi tahsil tutarı
    Description: dto.description,
    CreatedBy: dto.createdBy
  };
  
  const response = await accountingApi.post<ApiResponse<CheckNoteDto>>(
    `${API_BASE_URL}/api/accounting/CheckNote/change-status`,
    requestBody
  );
  
  if (response.data.success && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Durum değiştirilemedi');
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Durum rengini döndür
 */
export const getStatusColor = (status: CheckNoteStatusValue): string => {
  switch (status) {
    case CheckNoteStatus.PORTFOLIO:
      return 'primary';
    case CheckNoteStatus.IN_COLLECTION:
      return 'info';
    case CheckNoteStatus.COLLECTED:
      return 'success';
    case CheckNoteStatus.PARTIAL_COLLECTED:
      return 'warning';
    case CheckNoteStatus.ENDORSED:
      return 'secondary';
    case CheckNoteStatus.COLLATERAL:
      return 'default';
    case CheckNoteStatus.COLLATERAL_RETURNED:
      return 'primary';
    case CheckNoteStatus.BOUNCED:
      return 'error';
    case CheckNoteStatus.RETURNED:
      return 'warning';
    case CheckNoteStatus.CANCELED:
      return 'default';
    // Verilen çekler için renkler
    case CheckNoteStatus.GIVEN_PENDING:
      return 'warning';
    case CheckNoteStatus.GIVEN_PAID:
      return 'success';
    case CheckNoteStatus.GIVEN_RETURNED:
      return 'info';
    default:
      return 'default';
  }
};

/**
 * Durum adını Türkçe döndür
 */
export const getStatusLabel = (status: CheckNoteStatusValue): string => {
  switch (status) {
    case CheckNoteStatus.PORTFOLIO:
      return 'Portföyde';
    case CheckNoteStatus.IN_COLLECTION:
      return 'Tahsilde';
    case CheckNoteStatus.COLLECTED:
      return 'Tahsil Edildi';
    case CheckNoteStatus.PARTIAL_COLLECTED:
      return 'Kısmi Tahsil';
    case CheckNoteStatus.ENDORSED:
      return 'Ciro Edildi';
    case CheckNoteStatus.COLLATERAL:
      return 'Teminatta';
    case CheckNoteStatus.COLLATERAL_RETURNED:
      return 'Teminattan İade';
    case CheckNoteStatus.BOUNCED:
      return 'Karşılıksız';
    case CheckNoteStatus.RETURNED:
      return 'İade Edildi';
    case CheckNoteStatus.CANCELED:
      return 'İptal';
    // Verilen çekler için etiketler
    case CheckNoteStatus.GIVEN_PENDING:
      return 'Ödenmedi';
    case CheckNoteStatus.GIVEN_PAID:
      return 'Ödendi';
    case CheckNoteStatus.GIVEN_RETURNED:
      return 'İade Alındı';
    default:
      return status;
  }
};

/**
 * Tür adını Türkçe döndür
 */
export const getTypeLabel = (type: CheckNoteTypeValue): string => {
  switch (type) {
    case CheckNoteType.CHECK:
      return 'Çek';
    case CheckNoteType.PROMISSORY_NOTE:
      return 'Senet';
    default:
      return type;
  }
};

/**
 * Yön adını Türkçe döndür
 */
export const getDirectionLabel = (direction: CheckNoteDirectionValue): string => {
  switch (direction) {
    case CheckNoteDirection.RECEIVED:
      return 'Alınan';
    case CheckNoteDirection.GIVEN:
      return 'Verilen';
    default:
      return direction;
  }
};

/**
 * Para birimini formatla
 */
export const formatCurrency = (amount: number, currency: string = 'TRY'): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Tarihi formatla
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('tr-TR');
};

/**
 * Vadeye kalan gün hesapla
 */
export const calculateDaysUntilDue = (dueDateString: string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(dueDateString);
  dueDate.setHours(0, 0, 0, 0);
  const diffTime = dueDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Seri senet oluştur
 */
export const createBatchPromissoryNotes = async (dto: CreateBatchPromissoryNotesDto): Promise<BatchPromissoryNotesResponseDto> => {
  try {
    console.log('📤 [API] Seri senet oluşturuluyor:', dto);
    const response = await accountingApi.post<ApiResponse<BatchPromissoryNotesResponseDto>>(`${API_BASE_URL}/api/accounting/CheckNote/batch`, dto);
    console.log('📥 [API] Seri senet yanıtı:', response.data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Seri senet oluşturma başarısız');
  } catch (error: unknown) {
    console.error('❌ [API] Seri senet hatası:', error);
    if (axios.isAxiosError(error)) {
      console.error('❌ [API] Axios error details:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.message || 'Seri senet oluşturulurken hata oluştu');
    }
    throw error;
  }
};
